import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';

const MarkdownRenderer = ({ content, className = '' }) => {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeHighlight, rehypeRaw]}
        components={{
          // Headers
          h1: ({ children }) => (
            <h1 className="text-2xl font-bold text-slate-800 mb-4 mt-6 border-b border-slate-200 pb-2">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-bold text-slate-800 mb-3 mt-5 border-b border-slate-200 pb-1">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold text-slate-800 mb-2 mt-4">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-base font-semibold text-slate-800 mb-2 mt-3">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="text-sm font-semibold text-slate-800 mb-2 mt-3">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="text-sm font-medium text-slate-700 mb-2 mt-3">
              {children}
            </h6>
          ),
          
          // Paragraphs
          p: ({ children }) => (
            <p className="text-sm leading-relaxed text-slate-800 mb-3">
              {children}
            </p>
          ),
          
          // Lists
          ul: ({ children }) => (
            <ul className="list-disc list-outside mb-4 ml-6 space-y-2 pl-2">
              {children}
            </ul>
          ),
          ol: ({ children, start, ...props }) => (
            <ol 
              className="list-decimal list-outside mb-4 ml-6 space-y-2 pl-2" 
              start={start}
              style={{ counterReset: start ? `list-item ${start - 1}` : undefined }}
              {...props}
            >
              {children}
            </ol>
          ),
          li: ({ children, ...props }) => {
            // Gérer les listes imbriquées
            const hasNestedList = React.Children.toArray(children).some(
              child => child?.type === 'ul' || child?.type === 'ol'
            );
            
            return (
              <li className={`text-sm text-slate-800 leading-relaxed ${hasNestedList ? 'mb-2' : ''}`} {...props}>
                <span className="ml-2">{children}</span>
              </li>
            );
          },
          
          // Code blocks
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            if (inline) {
              return (
                <code 
                  className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-xs font-mono border"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            
            return (
              <div className="my-4">
                {language && (
                  <div className="bg-slate-700 text-slate-300 px-3 py-1 text-xs font-mono rounded-t-lg border-b border-slate-600">
                    {language}
                  </div>
                )}
                <pre className={`bg-slate-800 text-slate-100 p-4 overflow-x-auto text-sm font-mono ${language ? 'rounded-b-lg' : 'rounded-lg'}`}>
                  <code className={className} {...props}>
                    {children}
                  </code>
                </pre>
              </div>
            );
          },
          
          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border border-slate-300 rounded-lg overflow-hidden">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-slate-100">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="bg-white">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-slate-200">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-4 py-2 text-left text-sm font-semibold text-slate-800 border-r border-slate-200 last:border-r-0">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-4 py-2 text-sm text-slate-800 border-r border-slate-200 last:border-r-0">
              {children}
            </td>
          ),
          
          // Blockquotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-cyan-500 pl-4 py-2 my-4 bg-cyan-50 rounded-r-lg">
              <div className="text-slate-700 italic">
                {children}
              </div>
            </blockquote>
          ),
          
          // Horizontal rules
          hr: () => (
            <hr className="my-6 border-slate-300" />
          ),
          
          // Links
          a: ({ href, children }) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-300 hover:decoration-cyan-500 transition-colors"
            >
              {children}
            </a>
          ),
          
          // Strong and emphasis
          strong: ({ children }) => (
            <strong className="font-semibold text-slate-900">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-slate-700">
              {children}
            </em>
          ),
          
          // Task list items (GitHub style)
          input: ({ type, checked, ...props }) => {
            if (type === 'checkbox') {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  disabled
                  className="mr-2 rounded border-slate-300"
                  {...props}
                />
              );
            }
            return <input type={type} {...props} />;
          }
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;